import { apiRequest } from './apiClient';
import {
  LeitoOperacional,
  LeitoPayload,
  LeitosPageResponse,
  LeitoStatus,
  PerfilAssistencial,
  PerfilAssistencialCatalogo,
  PontoGeracaoLeito,
  PontoGeracaoPayload,
  SaldoCreditoLeito,
  AtivacaoLeitoResultado,
  CadeiaCompatibilidadePreview,
  CadeiaPapel,
  FornecedorElegivel,
} from '../types/leito';

export interface ListLeitosParams {
  page: number;
  pageSize: number;
  clienteEmpresaId?: string;
  status?: LeitoStatus;
  perfilAssistencial?: PerfilAssistencial;
  search?: string;
}

function query(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== '') search.set(key, String(value));
  });
  return search.toString();
}

export function listarLeitos(token: string, params: ListLeitosParams): Promise<LeitosPageResponse> {
  return apiRequest<LeitosPageResponse>(`/leitos?${query({
    page: params.page,
    pageSize: params.pageSize,
    clienteEmpresaId: params.clienteEmpresaId,
    status: params.status,
    perfilAssistencial: params.perfilAssistencial,
    search: params.search,
  })}`, token);
}

export function obterLeito(token: string, id: string): Promise<LeitoOperacional> {
  return apiRequest<LeitoOperacional>(`/leitos/${id}`, token);
}

export function criarLeitoOperacional(token: string, payload: LeitoPayload): Promise<LeitoOperacional> {
  return apiRequest<LeitoOperacional>('/leitos', token, { method: 'POST', body: JSON.stringify(payload) });
}

export function atualizarLeitoOperacional(token: string, id: string, payload: LeitoPayload): Promise<LeitoOperacional> {
  return apiRequest<LeitoOperacional>(`/leitos/${id}`, token, { method: 'PATCH', body: JSON.stringify(payload) });
}

export function desativarLeitoOperacional(token: string, id: string, motivo: string): Promise<LeitoOperacional> {
  return apiRequest<LeitoOperacional>(`/leitos/${id}/desativar`, token, { method: 'POST', body: JSON.stringify({ motivo }) });
}

export function listarPerfisLeito(token: string): Promise<{ items: PerfilAssistencialCatalogo[] }> {
  return apiRequest<{ items: PerfilAssistencialCatalogo[] }>('/leitos/perfis-assistenciais', token);
}

export function obterPontoGeracaoLeito(token: string, leitoId: string): Promise<PontoGeracaoLeito | null> {
  return apiRequest<PontoGeracaoLeito | null>(`/leitos/${leitoId}/ponto-geracao`, token);
}

export function salvarPontoGeracaoLeito(token: string, leitoId: string, payload: PontoGeracaoPayload): Promise<PontoGeracaoLeito> {
  return apiRequest<PontoGeracaoLeito>(`/leitos/${leitoId}/ponto-geracao`, token, { method: 'PUT', body: JSON.stringify(payload) });
}

export function obterSaldoLeito(token: string, clienteEmpresaId: string): Promise<SaldoCreditoLeito> {
  return apiRequest<SaldoCreditoLeito>(`/creditos/saldo?${query({ clienteEmpresaId })}`, token);
}

export function listarFornecedoresElegiveis(
  token: string,
  leitoId: string,
  papel: CadeiaPapel
): Promise<{ papel: CadeiaPapel; items: FornecedorElegivel[] }> {
  return apiRequest(`/leitos/${leitoId}/fornecedores-elegiveis?${query({ papel })}`, token);
}

export interface CadeiaFornecedorPayload {
  empresaId: string;
  papel: CadeiaPapel;
  ordem: number;
}

export function previewCadeiaLeito(
  token: string,
  leitoId: string,
  fornecedores: CadeiaFornecedorPayload[]
): Promise<CadeiaCompatibilidadePreview> {
  return apiRequest(`/leitos/${leitoId}/cadeia/compatibilidade`, token, {
    method: 'POST', body: JSON.stringify({ fornecedores }),
  });
}

export function salvarCadeiaLeito(
  token: string,
  leitoId: string,
  fornecedores: CadeiaFornecedorPayload[]
): Promise<unknown> {
  return apiRequest(`/leitos/${leitoId}/cadeia`, token, {
    method: 'PUT', body: JSON.stringify({ fornecedores }),
  });
}

export function solicitarAtivacaoLeito(token: string, leitoId: string): Promise<AtivacaoLeitoResultado> {
  return apiRequest(`/leitos/${leitoId}/ativar`, token, {
    method: 'POST', body: JSON.stringify({ referencia: `assistente_web:${leitoId}` }),
  });
}
